import type { SxProps, Theme, AccordionProps } from '@mui/material';
import {
  AccordionDetails,
  AccordionSummary,
  Accordion as AccordionMui,
  useTheme,
} from '@mui/material';
import type { PropsWithChildren, ReactNode } from 'react';
import React, { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import type { ITheme } from '../../styles/theme';
import { useSettingBasedOnBreakpoints } from '../../../hooks/use-setting-based-on-breakpoints';
import type { IPropsWithTestId } from '../../../constants/data-element-tracking-ids';
import {
  ELEMENT_ID_DATA_LOG_ATTRIBUTE,
  ELEMENT_ID_DATA_TEST_ATTRIBUTE,
} from '../../../constants/data-element-tracking-ids';

// Exporting this because there's a component in the calculator
// that's supposed to have the same shape and box shadow as its
// accordion sibling:
export const ACCORDION_BORDER_STYLES: SxProps<Theme> = {
  boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
  borderRadius: '10px',
  '&.MuiAccordion-root:first-of-type,&.MuiAccordion-root:last-of-type': {
    // This overrides default MUI behavior that changes the border radius for
    // first and last children
    // This is going to be a problem if someone ever tries to specify borderRadius
    // via additionalStyles
    borderRadius: '10px',
  },
};

export const ACCORDION_BORDERLESS_STYLES: SxProps<Theme> = {
  boxShadow: 'none',
  border: 'none',
  // This disables the 1px border that shows above the accordion:
  '&:before': {
    display: 'none',
  },
};

export interface IAccordion extends IPropsWithTestId {
  summaryContents: ReactNode;
  expanded?: boolean;
  expandIcon?: ReactNode;
  expandIconPosition?: 'start' | 'end';
  theme: ITheme;
  additionalStyles?: SxProps<Theme>;
  onChange?: (event: React.SyntheticEvent<Element>) => void;
  clickableDetails?: boolean;
  showBorder?: boolean;
}

const Accordion: React.FC<PropsWithChildren<IAccordion & AccordionProps>> = ({
  summaryContents,
  children,
  expanded,
  theme,
  additionalStyles,
  onChange,
  dataElementId,
  expandIcon = (
    <FontAwesomeIcon
      icon={faChevronDown}
      style={{ color: theme.colors.grayscale.inactiveGrey1 }}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...{ [ELEMENT_ID_DATA_TEST_ATTRIBUTE]: `${dataElementId}-expand-icon` }}
    />
  ),
  expandIconPosition = 'end',
  clickableDetails = false,
  showBorder = true,
  ...props
}) => {
  const muiTheme = useTheme();
  const { isMobile } = useSettingBasedOnBreakpoints(muiTheme);

  const styles = useMemo(
    () => ({
      ...(showBorder ? ACCORDION_BORDER_STYLES : ACCORDION_BORDERLESS_STYLES),
      ...(additionalStyles ?? {}),
    }),
    [additionalStyles, showBorder],
  );

  const summaryStyles = useMemo(
    () => ({
      padding: showBorder ? '15px 30px' : '0',
      minHeight: 'unset',
      alignItems: 'flex-start',
      flexDirection: expandIconPosition === 'start' ? 'row-reverse' : 'row',
      '.MuiAccordionSummary-expandIconWrapper': {
        margin:
          expandIconPosition === 'start'
            ? `5px ${isMobile ? '8' : '16'}px 5px 0`
            : `5px 0 5px ${isMobile ? '8' : '16'}px`,
      },
      '.MuiAccordionSummary-content,.MuiAccordionSummary-content.Mui-expanded':
        {
          margin: '0',
        },
      '&.Mui-expanded': {
        minHeight: 'unset',
      },
    }),
    [showBorder, expandIconPosition, isMobile],
  );

  const detailsStyles = useMemo(
    () => ({ padding: showBorder ? '1px 30px 15px' : '0' }),
    [showBorder],
  );

  return (
    <AccordionMui
      expanded={expanded}
      onChange={onChange}
      sx={styles}
      disableGutters={!showBorder}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...{
        [ELEMENT_ID_DATA_LOG_ATTRIBUTE]: dataElementId,
        [ELEMENT_ID_DATA_TEST_ATTRIBUTE]: dataElementId,
      }}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    >
      <AccordionSummary
        expandIcon={expandIcon}
        sx={summaryStyles}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...{
          [ELEMENT_ID_DATA_LOG_ATTRIBUTE]: `${dataElementId}-summary`,
          [ELEMENT_ID_DATA_TEST_ATTRIBUTE]: `${dataElementId}-summary`,
        }}
      >
        {summaryContents}
      </AccordionSummary>
      <AccordionDetails
        sx={detailsStyles}
        onClick={clickableDetails ? onChange : undefined}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...{ [ELEMENT_ID_DATA_TEST_ATTRIBUTE]: `${dataElementId}-details` }}
      >
        {children}
      </AccordionDetails>
    </AccordionMui>
  );
};

export default Accordion;
